import type { AxiosInstance } from 'axios';

import type { AppError } from '@base-shared/utils/error-handler';

export type RequestHandlerDependencies = {
  onError?: (error: AppError) => Promise<void>;
};

export type ApiDependencies = {
  getAxios?: () => AxiosInstance;
  getConnectionsAxios?: () => AxiosInstance;
};

export type CognitoAuthDependencies = {
  onAuthEvent?: (eventName: string) => Promise<void>;
};

type DependencyManagerInput = {
  requestHandler?: RequestHandlerDependencies;
  api?: ApiDependencies;
  auth?: CognitoAuthDependencies;
};

const unconfiguredAxiosResolver = () => {
  throw new Error('DependencyManager.api.getAxios is not configured.');
};

const unconfiguredConnectionsAxiosResolver = () => {
  throw new Error('DependencyManager.api.getConnectionsAxios is not configured.');
};

/**
 * Centralized runtime dependency registry shared across base-app modules.
 *
 * Purpose:
 * - Allow app bootstrap code (plugins/main entrypoints) to inject app-specific implementations once.
 *
 * Behavior:
 * - `set()` performs a shallow merge per dependency group (`requestHandler`, `api`, `auth`).
 * - `reset()` restores safe defaults and is primarily intended for test isolation.
 */
export const DependencyManager = {
  requestHandler: {
    onError: undefined as RequestHandlerDependencies['onError']
  },
  api: {
    getAxios: unconfiguredAxiosResolver as () => AxiosInstance,
    getConnectionsAxios: unconfiguredConnectionsAxiosResolver as () => AxiosInstance
  },
  auth: {
    onAuthEvent: undefined as CognitoAuthDependencies['onAuthEvent']
  },
  set(dependencies: DependencyManagerInput) {
    if (dependencies.requestHandler) {
      this.requestHandler = {
        ...this.requestHandler,
        ...dependencies.requestHandler
      };
    }

    if (dependencies.api) {
      this.api = {
        ...this.api,
        ...dependencies.api
      };
    }

    if (dependencies.auth) {
      this.auth = {
        ...this.auth,
        ...dependencies.auth
      };
    }
  },
  reset() {
    this.requestHandler = {
      onError: undefined
    };
    this.api = {
      getAxios: unconfiguredAxiosResolver,
      getConnectionsAxios: unconfiguredConnectionsAxiosResolver
    };
    this.auth = {
      onAuthEvent: undefined
    };
  }
};
